/**
 * Discriminated autocomplete types for preventative correction UI.
 * Maps 1:1 to components in preventative-correction-dialog (keyed by type, not field key).
 */
export const PreventativeAutocompleteType = {
    PART: 'part-autocomplete',
    EXTERNAL_PART: 'external-part-autocomplete',
    CUSTOMER: 'customer-autocomplete',
    EXTERNAL_CUSTOMER: 'external-customer-autocomplete',
    WAREHOUSE: 'warehouse-autocomplete',
    PARTNER: 'partner-autocomplete',
    TAG: 'tag-autocomplete',
    FORM: 'form-autocomplete',
    PRODUCT_RELATION: 'product-relation-autocomplete',
    VENDOR: 'vendor-autocomplete',
    TERRITORY: 'territory-autocomplete',
    SALES_PERSON: 'sales-person-autocomplete',
    INDUSTRY_PROPERTY_OPTIONS: 'industry-property-options-autocomplete',
};

/**
 * @param {string} [type]
 * @returns {boolean}
 */
export const isPreventativeAutocompleteType = (type) =>
    typeof type === 'string' && type.endsWith('-autocomplete');

/**
 * Optional creation/import dialogs tied to an autocomplete field type.
 * @param {string} [fieldType]
 * @returns {{ creationDialog: 'product'|'customer'|'vendor'|'tag'|null, importDialog: 'product'|'customer'|null }}
 */
/**
 * @param {string} [fieldType]
 * @returns {boolean}
 */
export const fieldSupportsAddPropertyOption = (fieldType) => fieldType === PreventativeAutocompleteType.INDUSTRY_PROPERTY_OPTIONS;

export const getPreventativeAutocompleteActionDialogs = (fieldType) => {
    switch (fieldType) {
        case PreventativeAutocompleteType.PART:
            return {creationDialog: 'product', importDialog: 'product'};
        case PreventativeAutocompleteType.CUSTOMER:
            return {creationDialog: 'customer', importDialog: 'customer'};
        case PreventativeAutocompleteType.VENDOR:
            return {creationDialog: 'vendor', importDialog: null};
        case PreventativeAutocompleteType.TAG:
            return {creationDialog: 'tag', importDialog: null};
        default:
            return {creationDialog: null, importDialog: null};
    }
};

/**
 * Base column width (px) for preventative correction UI, before accounting for action buttons.
 * @param field
 * @returns {number}
 */
export const getPreventativeEditableColumnBaseWidth = (field) => {
    if (field.type === 'toggle') {
        return 140;
    }
    return 160;
};

/**
 * Estimated column width (px) for the grid without scanning all rows.
 * @param {{ type?: string }} field
 * @returns {number}
 */
export const getPreventativeEditableColumnWidth = (field) => {
    const {creationDialog, importDialog} = getPreventativeAutocompleteActionDialogs(field.type);
    const baseWidth = getPreventativeEditableColumnBaseWidth(field);
    let actionSlots = 2; // set-for-all, set-for-remaining
    if (creationDialog) actionSlots += 1;
    if (importDialog) actionSlots += 1;
    if (fieldSupportsAddPropertyOption(field.type)) actionSlots += 1;
    return baseWidth + (actionSlots * 40);
};
